/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class PoolService {

    /**
     * Create draft Pool campaign from Hire opening
     * Creates a draft audience campaign in Pool from a Hire opening's pre-created audience. Returns the campaign UUID for redirect to the campaign editor.
     * @param requestBody
     * @returns any draft campaign created
     * @throws ApiError
     */
    public static postInternalApiPoolCreateCampaignFromHire(
        requestBody: {
            /**
             * The Hire funnel external_id
             */
            funnel_external_id: string;
            /**
             * The type of campaign to create
             */
            campaign_type: 'email' | 'sms';
        },
    ): CancelablePromise<{
        campaign_uuid?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/pool/create_campaign_from_hire',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `pool not enabled for the account, or the user has no Pool access`,
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * Describe the account's Pool in Hire audience rules
     * Returns the typed description of the audience rules behind the Available Pool talent count. The description is passed through from Pool without reshaping. A Pool failure answers `unavailable`, never an error status.
     * @returns any description ready, or unavailable
     * @throws ApiError
     */
    public static getInternalApiPoolCriteriaDescription(): CancelablePromise<({
        status: 'ready';
        /**
         * Slug of the signed-in account the description was read for. Clients compare a row's account against it.
         */
        account_slug: string;
        /**
         * The Pool in Hire criteria description produced by Pool, passed through as received.
         */
        description: any;
    } | {
        status: 'unavailable';
    })> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/pool/criteria_description',
            errors: {
                403: `pool in hire is not visible to the user`,
            },
        });
    }

    /**
     * Proxy a Pool talent summary for a Hire applicant
     * Proxies the Pool talent summary for the given applicant to the recruiter browser.
     * @param applicantExternalId
     * @param xAccessToken
     * @returns any pool talent summary with no synced talent
     * @throws ApiError
     */
    public static getInternalApiPoolApplicantsTalentSummary(
        applicantExternalId: string,
        xAccessToken?: string,
    ): CancelablePromise<{
        talentUuid: string | null;
        activeCampaigns: number;
        isPoolPaid?: boolean | null;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/pool/applicants/{applicant_external_id}/talent_summary',
            path: {
                'applicant_external_id': applicantExternalId,
            },
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            errors: {
                401: `account API token without a recruiter session is not authenticated`,
                403: `pool in hire not enabled`,
                404: `applicant not found`,
                422: `failed to fetch the pool talent summary from wx`,
            },
        });
    }

    /**
     * Read the account's Pool tier
     * Whether the account's Pool tier is paid, as WX reports it. `is_pool_paid` is null when the tier is unknown.
     * @returns any pool tier
     * @throws ApiError
     */
    public static getInternalApiPoolTier(): CancelablePromise<{
        is_pool_paid: boolean | null;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/pool/tier',
            errors: {
                403: `Pool is not available to this user`,
            },
        });
    }

    /**
     * Report Pool flag and applicant migration status
     * Reports the account's Pool flag state and applicant migration counts. Reachable regardless of wx-pool state, so WX can decide whether to offer free-tier provisioning. The counts are null when the bounded count query times out. `expected_talents` is the number of distinct applicant emails — the talents Pool holds once the account is fully migrated; `applicants_migrated` is the bulk-migration stamp, which under-reports live-synced applicants.
     * @param xAccessToken
     * @returns any pool status
     * @throws ApiError
     */
    public static getInternalApiWxPoolStatus(
        xAccessToken: string,
    ): CancelablePromise<{
        wx_pool_enabled: boolean;
        pool_in_hire_enabled: boolean;
        applicants_total: number | null;
        applicants_migrated: number | null;
        expected_talents: number | null;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/pool/status',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
        });
    }

    /**
     * Request sales outreach for the Free Pool tier Opening Matches upsell
     * Notifies sales, throttled to one accepted request per user per cooldown window.
     * @returns any request throttled inside the cooldown window
     * @throws ApiError
     */
    public static postInternalApiWxPoolFreemiumSalesNotifications(): CancelablePromise<{
        accepted: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/wx/pool_freemium_sales_notifications',
            errors: {
                401: `no recruiter session`,
                403: `pool in hire not enabled`,
            },
        });
    }

}